import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react'

import { useUnleashUserContextReady } from '@/components/UnleashProvider'

type FeatureFlagGuardProps = {
  flagName: string
  children: ReactNode
  fallback?: ReactNode
  redirectTo?: string
}

export function FeatureFlagGuard({ flagName, children, fallback, redirectTo = '/' }: FeatureFlagGuardProps) {
  const isEnabled = useFlag(flagName)
  const { flagsReady } = useFlagsStatus()
  const isUserContextReady = useUnleashUserContextReady()
  const navigate = useNavigate()

  // On a hard page load, flags are fetched asynchronously and the Clerk user
  // context is pushed even later. Until both settle, useFlag can report a
  // spurious false for user-sticky rollouts. Hold rendering (and especially
  // the redirect) until the verdict is real.
  const isSettled = flagsReady && isUserContextReady

  useEffect(() => {
    // Only redirect if flag is disabled and no fallback provided
    if (isSettled && !isEnabled && !fallback) {
      navigate(redirectTo)
    }
  }, [isSettled, isEnabled, fallback, redirectTo, navigate])

  if (!isSettled) {
    return null
  }

  if (!isEnabled) {
    if (fallback) return <>{fallback}</>
    return null // Will redirect via useEffect
  }

  return <>{children}</>
}
